import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailWarning, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import authService from '@/services/authService.ts';
import { useAuth } from '@/context/AuthContext.tsx';

/**
 * Aviso "verifica tu correo" para usuarios autenticados sin email verificado.
 * Las simulaciones están bloqueadas en backend hasta verificar (MVP).
 */
export default function EmailVerificationBanner() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (!isAuthenticated || !user || user.emailVerified) return null;

  const resend = async () => {
    setStatus('sending');
    try {
      await authService.resendVerification();
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      role="alert"
      className="mx-auto flex w-full max-w-4xl flex-col items-start gap-3 rounded-xl border border-amber-400/50 bg-amber-50 px-4 py-3 text-sm dark:border-amber-500/30 dark:bg-amber-950/30 sm:flex-row sm:items-center"
    >
      <MailWarning className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1">
        <p className="font-medium text-amber-900 dark:text-amber-200">
          {t('auth.verifyBannerTitle')}
        </p>
        <p className="text-amber-800/80 dark:text-amber-300/70">
          {t('auth.verifyBannerDesc', { email: user.email })}
        </p>
        {status === 'error' && (
          <p className="mt-1 text-destructive">{t('auth.verifyBannerError')}</p>
        )}
      </div>
      {status === 'sent' ? (
        <span className="inline-flex items-center gap-1.5 font-medium text-green-700 dark:text-green-400">
          <CheckCircle className="size-4" />
          {t('auth.verifyBannerSent')}
        </span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={resend}
          disabled={status === 'sending'}
          className="shrink-0"
        >
          {status === 'sending' && <Loader2 className="size-4 animate-spin" />}
          {t('auth.verifyBannerResend')}
        </Button>
      )}
    </div>
  );
}
